import React, { useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SearchButtonProps {
  isExpanded: boolean;
  onToggle: () => void;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Reusable search button component.
 * Shows a magnifying glass icon that expands into a text field when clicked.
 */
const SearchButton: React.FC<SearchButtonProps> = ({
  isExpanded,
  onToggle,
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  if (isExpanded) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="relative flex items-center">
          <MagnifyingGlassIcon className="absolute left-3 h-4 w-4 text-disabled" />
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="pl-9 pr-8 py-2 border border-edge-input rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 w-48 sm:w-64"
          />
          <button
            onClick={() => {
              onChange('');
              onToggle();
            }}
            className="absolute right-2 text-disabled hover:text-body"
            aria-label="Close search"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      data-track="search_open"
      onClick={onToggle}
      className={`relative inline-flex items-center justify-center w-10 h-10 border rounded-md transition-colors ${
        value
          ? 'bg-primary-50 border-primary-300 text-primary-700'
          : 'bg-surface border-edge-input text-body hover:bg-surface-hover'
      } ${className}`}
      aria-label="Open search"
    >
      <MagnifyingGlassIcon className="h-5 w-5" />
      {value && (
        <span className="absolute top-0.5 right-0.5 inline-flex items-center justify-center w-2.5 h-2.5 rounded-full bg-primary-600"></span>
      )}
    </button>
  );
};

export default SearchButton;
